import {ChangeDetectionStrategy, Component, InputSignal, Signal, WritableSignal, computed, inject, input, signal} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ContentComponent} from '../../../../shared/elements/content/content.component';
import {FormButtonComponent} from '../../../../shared/elements/form-button/form-button.component';
import {FormLabelComponent} from '../../../../shared/elements/form-label/form-label.component';
import {FormTextareaComponent} from '../../../../shared/elements/form-textarea/form-textarea.component';
import {HeadlineComponent} from '../../../../shared/elements/headline/headline.component';
import {TooltipComponent} from '../../../../shared/elements/tooltip/tooltip.component';
import {TranslatePipe} from '../../../../shared/pipes/translate.pipe';
import {PreviewCommentField} from '../../../../shared/types/feedback-types/previewCommentField.interface';
import {FeedbackQuestionType} from '../../../../shared/types/generated/feedbackQuestionType';
import {FileStorageResource} from '../../../../shared/types/generated/fileStorageResource';
import {DialogDocumentsAttachmentsComponent} from '../../../documents/dialog-documents-attachments/dialog-documents-attachments.component';
import {FeedbackStructureQuestionFormQuestion, FeedbackStructureQuestionOption} from '../../feedback-structure-create-update-dialog/feedback-structure-question/feedback-structure-question-form.model';
import {FeedbackLogicRuleTextBuilderService} from '../../../../shared/services/feedback-logic-rule-text-builder.service';
import {FeedbackFormElementDateComponent} from '../feedback-form-element-date/feedback-form-element-date.component';
import {FeedbackFormElementDateTimeComponent} from '../feedback-form-element-date-time/feedback-form-element-date-time.component';
import {FeedbackFormElementDropdownComponent} from '../feedback-form-element-dropdown/feedback-form-element-dropdown.component';
import {FeedbackFormElementFilesComponent} from '../feedback-form-element-files/feedback-form-element-files.component';
import {FeedbackFormElementMultiSelectionComponent} from '../feedback-form-element-multi-selection/feedback-form-element-multi-selection.component';
import {FeedbackFormElementOrderingComponent} from '../feedback-form-element-ordering/feedback-form-element-ordering.component';
import {FeedbackFormElementRatingComponent} from '../feedback-form-element-rating/feedback-form-element-rating.component';
import {FeedbackFormElementSingleSelectionComponent} from '../feedback-form-element-single-selection/feedback-form-element-single-selection.component';
import {FeedbackFormElementSliderComponent} from '../feedback-form-element-slider/feedback-form-element-slider.component';
import {FeedbackFormElementTextInputComponent} from '../feedback-form-element-text-input/feedback-form-element-text-input.component';
import {FeedbackFormElementTimeComponent} from '../feedback-form-element-time/feedback-form-element-time.component';
import {feedbackCommentControlName, feedbackOptionCommentControlName, feedbackAnswerControlName, feedbackOptionControlName} from '../../helpers/feedback-structure-preview-control-names';

@Component({
	selector: 'app-feedback-question-wrapper',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		ContentComponent,
		DialogDocumentsAttachmentsComponent,
		FeedbackFormElementDateComponent,
		FeedbackFormElementDateTimeComponent,
		FeedbackFormElementDropdownComponent,
		FeedbackFormElementFilesComponent,
		FeedbackFormElementMultiSelectionComponent,
		FeedbackFormElementOrderingComponent,
		FeedbackFormElementRatingComponent,
		FeedbackFormElementSingleSelectionComponent,
		FeedbackFormElementSliderComponent,
		FeedbackFormElementTextInputComponent,
		FeedbackFormElementTimeComponent,
		FormButtonComponent,
		FormLabelComponent,
		FormTextareaComponent,
		HeadlineComponent,
		TooltipComponent,
		TranslatePipe,
	],
	styles: [`
		.fade-in {
			animation: fadeIn 0.5s ease-in-out forwards;
			opacity: 0;
		}

		@keyframes fadeIn {
			from {
				opacity: 0;
				transform: translateY(10px);
			}

			to {
				opacity: 1;
				transform: translateY(0);
			}
		}

		.multicard-display {
			border: 1px solid var(--our-border-color);
			border-radius: var(--our-border-radius);
			padding: var(--our-cards-body-padding);
			margin-bottom: 1rem;

			.question-description {
				margin-top: 0.5rem;
				margin-bottom: 1rem;
			}
		}

		.oneform-display {
			.question-title {
				margin-top: 1.5rem;
			}

			.question-description {
				margin-top: 0.5rem;
				margin-bottom: 1rem;
			}
		}

		.one-at-atime {
			.question-description {
				margin-top: 0.5rem;
				margin-bottom: 1rem;
			}
		}
	`],
	templateUrl: './feedback-question-wrapper.component.html',
})
export class FeedbackQuestionWrapperComponent {
	private feedbackLogicRuleTextBuilder: FeedbackLogicRuleTextBuilderService = inject(FeedbackLogicRuleTextBuilderService);

	public question: InputSignal<FeedbackStructureQuestionFormQuestion> = input.required();
	public allQuestions: InputSignal<FeedbackStructureQuestionFormQuestion[]> = input<FeedbackStructureQuestionFormQuestion[]>([]);
	public questionNumber: InputSignal<number> = input.required();
	public formGroup: InputSignal<FormGroup> = input.required();
	public presentationClass: InputSignal<string> = input.required();
	public controlNamePrefix: InputSignal<string> = input('preview');
	public showAffectedRuleIcon: InputSignal<boolean> = input(true);
	public showRequiredAnswerError: InputSignal<boolean> = input(false);
	public disabled: InputSignal<boolean> = input(false);

	public FeedbackQuestionType: typeof FeedbackQuestionType = FeedbackQuestionType;
	public documentsAttachmentsDialogVisible: WritableSignal<boolean> = signal(false);

	public questionAttachmentFiles: Signal<FileStorageResource[]> = computed(() => this.question().files ?? []);
	public attachmentsAvailable: Signal<boolean> = computed(() => this.questionAttachmentFiles().length > 0);
	public affectedRuleSummaries: Signal<string[]> = computed(() => this.feedbackLogicRuleTextBuilder.buildAffectedRuleTexts(
		this.question(),
		this.allQuestions()
	));
	public hasAffectedRules: Signal<boolean> = computed(() => this.affectedRuleSummaries().length > 0);
	public affectedRuleTooltipText: Signal<string> = computed(() => this.feedbackLogicRuleTextBuilder.buildAffectedRuleTooltipText(
		this.question(),
		this.allQuestions()
	));
	public affectedRuleTooltipClass: Signal<string> = computed(() => (
		`affected-rule-icon feedback-affected-rule-icon-${this.controlNamePrefix()}-${this.question().position}`
	));
	public affectedRuleTooltipTarget: Signal<string> = computed(() => (
		`.feedback-affected-rule-icon-${this.controlNamePrefix()}-${this.question().position}`
	));

	public questionContainerClasses(): string {
		const mandatoryClass = this.question().required ? ' mandatory_question' : '';

		return `${this.presentationClass()} d-flex align-items-start flex-column fade-in${mandatoryClass}`;
	}

	public shouldShowQuestionTitle(): boolean {
		return this.hasQuestionTitle() || this.shouldShowQuestionNumber() || this.shouldShowAffectedRuleIcon();
	}

	public shouldShowQuestionNumber(): boolean {
		return this.presentationClass() !== 'oneform-display' || this.hasQuestionTitle();
	}

	public shouldShowAffectedRuleIcon(): boolean {
		return this.showAffectedRuleIcon() && this.hasAffectedRules();
	}

	public commentControlName(): string {
		return feedbackCommentControlName(this.question(), this.controlNamePrefix());
	}

	public commentFields(fallbackLabel: string): PreviewCommentField[] {
		const question = this.question();
		const fields: PreviewCommentField[] = [];

		if (this.hasQuestionCommentConfiguration(question) && question.configuration.commentable) {
			fields.push({
				controlName: this.commentControlName(),
				label: this.commentLabel(question.configuration.comment_prompt, fallbackLabel),
			});
		}

		question.options
			.filter((option) => this.isSelectedCommentableOption(question, option))
			.forEach((option) => {
				fields.push({
					controlName: feedbackOptionCommentControlName(question, option, this.controlNamePrefix()),
					label: this.commentLabel(option.comment_prompt, fallbackLabel),
				});
			});

		return fields;
	}

	public shouldShowRequiredAnswerError(): boolean {
		if (!this.showRequiredAnswerError()) {
			return false;
		}

		switch (this.question().type) {
			case FeedbackQuestionType.SINGLE_SELECTION:
			case FeedbackQuestionType.MULTI_SELECTION:
				return true;
			default:
				return false;
		}
	}

	private isSelectedCommentableOption(
		question: FeedbackStructureQuestionFormQuestion,
		option: FeedbackStructureQuestionOption
	): boolean {
		if (option.commentable !== true) {
			return false;
		}

		switch (question.type) {
			case FeedbackQuestionType.SINGLE_SELECTION:
			case FeedbackQuestionType.DROPDOWN:
				return this.formGroup().get(feedbackAnswerControlName(question, this.controlNamePrefix()))?.value === option.position;
			case FeedbackQuestionType.MULTI_SELECTION:
				return this.formGroup().get(feedbackOptionControlName(question, option, this.controlNamePrefix()))?.value === true;
			case FeedbackQuestionType.ORDERING:
				return true;
			default:
				return false;
		}
	}

	private commentLabel(commentPrompt: string | null | undefined, fallbackLabel: string): string {
		const label = commentPrompt?.trim();
		return label ? label : fallbackLabel;
	}

	private hasQuestionTitle(): boolean {
		return (this.question().title ?? '').trim() !== '';
	}

	private hasQuestionCommentConfiguration(
		question: FeedbackStructureQuestionFormQuestion
	): question is FeedbackStructureQuestionFormQuestion & {configuration: {commentable: boolean; comment_prompt: string | null}} {
		return 'commentable' in question.configuration;
	}
}
